/**
 * Medewerker-kostprijs — DTO's, berekenings-types + Zod-schemas.
 *
 * Conform docs/specs/20-calculatie-offerte-tool.md §2.2 + de batch-4 spec
 * `medewerker-kostprijs-autofill-opbouw`. De pure berekening leeft in
 * `@streep/utils` (`computeEmployeeCost` + de `employee-cost-autofill`-helpers);
 * deze module levert de gedeelde types + de API-DTO's. Gevoelige financiële data
 * (salaris + kostprijsopbouw) is serverside afgeschermd + geaudit.
 */

import { z } from 'zod'

// ─── Pure berekenings-types (spec 20 §2.2.2 + batch-4 autofill) ──────────────

/// Invoer voor `computeEmployeeCost`. Alle bedragen per maand (loon/voertuig/
/// overig); de berekening rekent ze om naar jaarbasis.
export interface EmployeeCostInput {
  brutoMaandloon: number
  dertiendeMaand: boolean
  dertiendeMaandPct: number // bijv. 8.33 voor 1/12
  /// Contracturen per week (bron: actief contract — `Employee.contractHoursPerWeek`
  /// / contractmutatie, dezelfde bron als de capacity-engine). Samen met
  /// `CostParameters.voltijdsUrenPerWeek` bepaalt dit de FTE-factor waarmee de
  /// declarabele uren (deler) én de opleidingskosten worden geschaald. Een
  /// fulltimer (= voltijdsuren) krijgt fteFactor 1,0 → ongewijzigd t.o.v. de
  /// bedrijfsbrede declarabele-uren-parameter. `0` ⇒ fteFactor 0 ⇒ kostprijs
  /// onbekend (null, PO-keuze Q2; nooit gokken; fix-kostprijs-per-uur deel 1).
  contractUrenPerWeek: number
  /// Absolute per-persoon override van de declarabele uren/jaar (deler). Gezet ⇒
  /// die waarde wordt direct als deler gebruikt (al inclusief eventuele parttime);
  /// leeg ⇒ `declarabeleUrenJaar × fteFactor`. Opleiding blijft hoe dan ook met de
  /// echte fteFactor geschaald. Optioneel (default: geen override).
  declarabeleUrenOverride?: number | null
  // Voertuigkosten per maand
  leaseBedragMaand: number
  brandstofBudgetMaand: number
  verzekeringMaand: number
  onderhoudMaand: number
  eigenAutoVergoeding: number
  /// Afschrijving per maand bij een KOOP-auto ((aanschaf - restwaarde)/jaren/12).
  /// Lease => 0 (geen eigendom, geen afschrijving). Batch-4 deel 2. Optioneel
  /// (default 0) zodat bestaande call-sites niet breken.
  afschrijvingMaand?: number
  // Overige maandkosten
  telefoonMaand: number
  /// Elektronica (laptop/tablet e.d.) per maand: waarde/60 (afschrijving 5 jaar).
  /// Batch-4 deel 3. Optioneel (default 0).
  elektronicaMaand?: number
  gereedschapMaand: number
  kledingMaand: number
  overigeKostenMaand: number
}

/// Bedrijfsbrede parameters (afgeleid uit CalculationParameter / een
/// CalculationParameterSet) die de kostprijsberekening aansturen.
export interface CostParameters {
  werkgeverlastenPct: number // 26
  vakantiegeldPct: number // 0
  pensioenPct: number // 0
  opleidingPerFte: number // 2500
  ziektePct: number // 5
  overheadPct: number // 20
  /// Declarabele uren/jaar voor een VOLTIJDER (FTE 1,0). Parttimers schalen via
  /// `EmployeeCostInput.contractUrenPerWeek / voltijdsUrenPerWeek`.
  declarabeleUrenJaar: number // 1600
  /// Voltijdsuren per week (FTE 1,0). Eén bron = `VerlofBeleid.voltijdsUren` via
  /// `getVoltijdsUrenForEmployee` (cascade, fallback 38 — CAO Metaal en
  /// Techniek, FO-150) — dezelfde resolver die
  /// `EmployeeSalaryMutation.parttimeFactor` voedt, zodat kostprijs-FTE en
  /// payroll-FTE één waarheid zijn. Default 38 (= de cascade-fallback) voor
  /// niet-medewerker-specifieke calculatie-contexten.
  voltijdsUrenPerWeek: number // 38
}

/// Volledige breakdown zoals de Excel (loonkosten → autokosten → totaal →
/// kostprijs/uur).
export interface EmployeeCostResult {
  brutoJaarloon: number
  dertiendeMaandBedrag: number
  werkgeverslasten: number
  vakantiegeld: number
  pensioen: number
  subtotaalLoon: number
  opleiding: number
  ziekteverzuim: number
  overhead: number
  loonkostenJaar: number

  leaseJaar: number
  brandstofJaar: number
  verzekeringJaar: number
  onderhoudJaar: number
  afschrijvingJaar: number
  eigenAutoJaar: number
  autokostenJaar: number

  telefoonJaar: number
  elektronicaJaar: number
  gereedschapJaar: number
  kledingJaar: number
  overigeJaar: number
  bijkomendeKostenJaar: number

  totaleKostenJaar: number
  /// FTE-factor = contracturen/week ÷ voltijdsuren/week (0 bij onbekende/0
  /// contracturen). Schaalt de declarabele uren + opleidingskosten.
  fteFactor: number
  /// Effectieve declarabele uren/jaar (de deler): de override indien gezet,
  /// anders `declarabeleUrenJaar × fteFactor`. 0 ⇒ kostprijs/uur onbekend (null).
  declarabeleUren: number
  /// Integrale kostprijs per uur, of `null` = "kostprijs onbekend" wanneer er geen
  /// declarabele uren zijn (0 contracturen zonder override, of een 0-parameter).
  /// PO-keuze Q2: bewust géén €0 — een 0-kostprijs leest stroomafwaarts als 100%
  /// marge. Calculaties/dashboards behandelen null apart ("onbekend"), niet als 0.
  kostprijsPerUur: number | null
}

/// Salaris-herleidbare velden van het loon-blok. Voor viewers mét breakdown-
/// recht maar zónder salaris-recht (`canViewSalary === false`) maskeert de API
/// deze serverside naar `null` (batch-4 deel 6). `opleiding` blijft zichtbaar
/// (vast bedrag per FTE, niet salaris-herleidbaar); `overhead`/`ziekteverzuim`
/// zijn percentages over het loon-subtotaal en dus wél herleidbaar → dicht.
export const SALARY_DERIVED_BREAKDOWN_FIELDS = [
  'brutoJaarloon',
  'dertiendeMaandBedrag',
  'werkgeverslasten',
  'vakantiegeld',
  'pensioen',
  'subtotaalLoon',
  'ziekteverzuim',
  'overhead',
  'loonkostenJaar',
] as const
export type SalaryDerivedBreakdownField = (typeof SALARY_DERIVED_BREAKDOWN_FIELDS)[number]

/// Breakdown zoals de API 'm teruggeeft: de salaris-herleidbare loonvelden zijn
/// `number | null` (gemaskeerd bij `canViewSalary === false`). Aggregaten
/// (autokosten/bijkomend/totaal/kostprijs-per-uur) blijven altijd gevuld — dát
/// is de feature voor de teamleider. Een volledige `EmployeeCostResult` is
/// assignable aan dit type.
export type EmployeeCostBreakdownDTO = Omit<EmployeeCostResult, SalaryDerivedBreakdownField> & {
  [K in SalaryDerivedBreakdownField]: number | null
}

// ─── Autofill (batch-4 medewerker-kostprijs-autofill) ────────────────────────

/// Parameters voor de auto-afleiding van kosten (los van de kern-formule). Vaste
/// afschrijvings-/credit-regels uit de spec; aanvulbaar via de parameter-set.
export interface AutofillParameters {
  werkdagenJaar: number // 220
  brandstofPerLiter: number // 2.15
  gemVerbruikLiter100km: number // 8
  autoAfschrijvingJaren: number // 5 (koop)
  telefoonAfschrijvingMaanden: number // 60
  elektronicaAfschrijvingMaanden: number // 60
  gereedschapAfschrijvingMaanden: number // 24
  kledingCreditPerKwartaal: number // 350 -> /3 per maand
}

/// Herkenning van een auto-waarde: AUTO (afgeleid uit de bron) of HANDMATIG
/// (bewust overschreven, met reden + audit).
export const COST_INPUT_SOURCES = ['AUTO', 'HANDMATIG'] as const
export type CostInputSource = (typeof COST_INPUT_SOURCES)[number]

/// Auto-afgeleide voertuigkosten (één waarheid = voertuigenmodule). `allInLease`
/// => alleen leasebedrag (excl. boetes/tankpas); anders onderhoud/verzekering los.
export interface VehicleCostDerivation {
  voertuigId: string | null
  kenteken: string | null
  leaseMaatschappij: string | null
  isAllInLease: boolean
  isKoop: boolean
  heeftTankpas: boolean
  bijtelling: boolean | null
  bijtellingPercentage: number | null
  leaseBedragMaand: number
  verzekeringMaand: number
  onderhoudMaand: number
  afschrijvingMaand: number
  // True wanneer brandstof al in de lease/tankpas zit (geen aparte brandstofpost).
  brandstofInbegrepen: boolean
}

/// Auto-afgeleide bijkomende kosten (telefoon/elektronica/kleding/gereedschap).
export interface BijkomendeCostDerivation {
  telefoonMaand: number
  elektronicaMaand: number
  gereedschapMaand: number
  kledingMaand: number
  // Bronwaarden (aanschafwaarde) voor de UI-uitleg; null = onbekend (handmatig).
  telefoonWaarde: number | null
  elektronicaWaarde: number | null
  gereedschapWaarde: number | null
  // Lopende telefoon-/data-abonnementen op naam (excl. btw, per maand); zit in telefoonMaand.
  telefoonAbonnementMaand: number | null
}

/// Volledige auto-afleiding voor één medewerker (provenance voor de UI).
export interface EmployeeCostAutofillDTO {
  // Salaris
  salarisBron: 'GROSS_MONTHLY' | 'SALARY_MUTATION' | 'GEEN'
  brutoMaandloonAuto: number | null
  brutoJaarloonAuto: number | null
  // Voertuig
  voertuig: VehicleCostDerivation | null
  // Bijkomende kosten
  bijkomende: BijkomendeCostDerivation
  // Brandstof woon-werk (per maand) — 0 wanneer all-in lease/tankpas dit dekt.
  brandstofWoonWerkMaand: number
  woonWerkAfstandKm: number | null
  // FTE-provenance (fix-kostprijs-per-uur deel 1). Contracturen/week uit het
  // contract; voltijdsuren/week uit `getVoltijdsUrenForEmployee` (VerlofBeleid-
  // cascade, fallback 38 — CAO, FO-150); fteFactor = contract/voltijds (null bij
  // onbekende contracturen → val terug op de bedrijfsbrede declarabele-uren-parameter).
  contractUrenPerWeek: number | null
  voltijdsUrenPerWeek: number
  fteFactor: number | null
}

// ─── API-DTO's (spec 20 §2.2.3 / §2.2.4 + batch-4) ───────────────────────────

export const AUTO_TYPES = ['lease', 'eigen', 'pool', 'geen'] as const
export type AutoType = (typeof AUTO_TYPES)[number]

/// Het volledige opgeslagen kostprofiel (invoervelden + gesnapshotte berekende
/// waarden + autofill-bron-vlaggen) voor `GET /employees/:id/cost-profile`.
export interface EmployeeCostProfileDTO {
  id: string
  userId: string
  brutoMaandloon: number | null
  dertiendeMaand: boolean
  dertiendeMaandPct: number
  autoType: string | null
  leaseBedragMaand: number | null
  brandstofBudgetMaand: number | null
  verzekeringMaand: number | null
  onderhoudMaand: number | null
  afschrijvingMaand: number | null
  eigenAutoVergoeding: number | null
  telefoonMaand: number | null
  elektronicaMaand: number | null
  gereedschapMaand: number | null
  kledingMaand: number | null
  overigeKostenMaand: number | null
  // Autofill-bron-vlaggen + reden (batch-4).
  salarisHandmatig: boolean
  salarisReden: string | null
  autokostenHandmatig: boolean
  autokostenReden: string | null
  bijkomendeHandmatig: boolean
  bijkomendeReden: string | null
  brandstofHandmatig: boolean
  brandstofReden: string | null
  declarabeleUrenJaar: number | null
  overheadPctOverride: number | null
  autofillVoertuigId: string | null
  // Berekende snapshots
  loonkostenJaar: number | null
  autokostenJaar: number | null
  totaleKostenJaar: number | null
  kostprijsPerUur: number | null
  updatedAt: string
}

/// Rij in het kostprijs-overzicht `/medewerkers/kostprijzen` (spec 20 §2.2.4).
export interface EmployeeCostSummaryRowDTO {
  userId: string
  employeeId: string | null
  name: string
  functie: string | null
  brutoMaandloon: number | null
  autokostenJaar: number | null
  totaleKostenJaar: number | null
  kostprijsPerUur: number | null
}

/// Geaggregeerde gemiddelden per functiegroep (samenvattingsrij).
export interface EmployeeCostFunctionAverageDTO {
  functie: string
  count: number
  gemKostprijsPerUur: number | null
}

/// `PATCH /employees/:id/cost-profile` — alle invoervelden optioneel (partial
/// update), berekende velden worden server-side opnieuw afgeleid. Bedragen
/// niet-negatief; `null` wist een veld. Bron-vlaggen sturen of een veld de bron
/// volgt (AUTO) of bewust is overschreven (HANDMATIG, met reden + audit).
export const employeeCostProfileUpdateSchema = z.object({
  brutoMaandloon: z.number().min(0).nullable().optional(),
  dertiendeMaand: z.boolean().optional(),
  dertiendeMaandPct: z.number().min(0).max(100).optional(),
  autoType: z.enum(AUTO_TYPES).nullable().optional(),
  leaseBedragMaand: z.number().min(0).nullable().optional(),
  brandstofBudgetMaand: z.number().min(0).nullable().optional(),
  verzekeringMaand: z.number().min(0).nullable().optional(),
  onderhoudMaand: z.number().min(0).nullable().optional(),
  afschrijvingMaand: z.number().min(0).nullable().optional(),
  eigenAutoVergoeding: z.number().min(0).nullable().optional(),
  telefoonMaand: z.number().min(0).nullable().optional(),
  elektronicaMaand: z.number().min(0).nullable().optional(),
  gereedschapMaand: z.number().min(0).nullable().optional(),
  kledingMaand: z.number().min(0).nullable().optional(),
  overigeKostenMaand: z.number().min(0).nullable().optional(),
  // Autofill-bron-vlaggen + reden
  salarisHandmatig: z.boolean().optional(),
  salarisReden: z.string().max(500).nullable().optional(),
  autokostenHandmatig: z.boolean().optional(),
  autokostenReden: z.string().max(500).nullable().optional(),
  bijkomendeHandmatig: z.boolean().optional(),
  bijkomendeReden: z.string().max(500).nullable().optional(),
  brandstofHandmatig: z.boolean().optional(),
  brandstofReden: z.string().max(500).nullable().optional(),
  declarabeleUrenJaar: z.number().min(0).nullable().optional(),
  overheadPctOverride: z.number().min(0).max(100).nullable().optional(),
})

export type EmployeeCostProfileUpdateInput = z.infer<typeof employeeCostProfileUpdateSchema>

/// Resultaat-shape van GET/PATCH cost-profile: profiel + breakdown + autofill +
/// of de viewer de opbouw/salaris mag zien (afscherming, batch-4 deel 6).
/// Bij `canViewSalary === false` zijn de salaris-herleidbare velden serverside
/// gemaskeerd: `profile.brutoMaandloon`/`salarisReden`/`loonkostenJaar` → null,
/// de loonvelden in `breakdown` → null en `autofill.brutoMaandloonAuto`/
/// `brutoJaarloonAuto` → null. Aggregaten blijven gevuld.
export interface EmployeeCostProfileResponse {
  profile: EmployeeCostProfileDTO
  breakdown: EmployeeCostBreakdownDTO
  autofill: EmployeeCostAutofillDTO
  canViewSalary: boolean
  canViewBreakdown: boolean
}
